import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionSummary,Button,FormControl,IconButton,InputLabel,MenuItem,Select,Stack,Tooltip,Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillCopy,AiFillDelete,AiFillTool } from 'react-icons/ai';
import { FaNetworkWired } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import Places,{ ConjuntoType,PlaceType } from '../../service/Places';
import queryClient from '../../service/query';
import Set from '../../service/Set';
import { setObjType } from '../../types/places/types';
import PlaceTypeAssoci from './Modal/Associacoes/PlaceTypeAssoci';
import NewModal from './Modal/NewModal';
import NewPlaceModal from './Modal/NewPlaceModal';
import RemoveModal from './Modal/RemoveModal';
import { reorderType } from './PlacesComponent';
import SetAcordeon from './SetAcordeon';

const SetService = new Set();
const PlacesServices = new Places();

moment.locale('pt-br');

export type placeTypeToSend = {
  id: string,
  lat: number,
  lng: number,
  localizacao: string,
  nome: string,
};

type PlacesAcordeonType = {
  place: PlaceType,
  selected: string[],
  setSelected: Function,
  handleChange: Function,
  expanded: string | false,
  local: reorderType,
  setLocal: Function,
  setCurrentPage: Function
};

export default function PlacesAcordeon({
  place,
  selected,
  setSelected,
  handleChange,
  expanded,
  local,
  setLocal,
  setCurrentPage
}
  :
  PlacesAcordeonType
) {
  const { customerId, placesList, setPlacesList } = useContext(UserContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [attSets, setAttSets] = useState(false);
  const { hierarchyLabel } = useContext(UserContext);
  const [openRemove, setOpenRemove] = useState(false);
  const [associationModal, setAssociationModal] = useState(false);
  const [expandedConjs, setExpandedConjs] = useState<boolean[]>(place?.conjuntosList?.map(conj => false) ?? []);
  const [reorderBy, setReorderBy] = useState<string>(local[place?.id ?? ''] ?? 'crescent');
  const [open, setOpen] = useState(false);

  const { t } = useTranslation('translation');

  useEffect(() => {
    if (attSets) {
      setAttSets(false);
    }
  }, [attSets, selected, place.conjuntosList]);

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationKey: ['DELETE_PLACE'],
    mutationFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');

      const promise = PlacesServices.deleteLocal(token, place?.id ?? '', customerId)
        .then(() => true);

      return toast.promise(
        promise,
        {
          pending: t('Services.ServicePlaces.removingPleaseWait'),
          success: `${hierarchyLabel.local} ${t('deleted')}!`,
          error: t('Services.ServicePlaces.errorDeleting')
        },
        {
          toastId: `delete-place-${place?.id}`
        }
      );
    },
    onSuccess: () => {
      trackEventMatomo('gestão de locais', 'sucesso', 'exclusão', 'local removido');
      setSelected(selected.filter((e) => e !== place.id));
      setCurrentPage(1);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
      setOpenRemove(false);
    },
    onError: () => trackEventMatomo('gestão de locais', 'erro', 'exclusão', 'falha ao remover local'),
  });

  const { mutate: create, isPending: isPendingCreate } = useMutation({
    mutationKey: ['CREATE_SET'],
    mutationFn: async (name: string) => {

      const token = await AttToken();
      if (!token) {
        throw new Error('Token not found');
      }

      const setObj: setObjType = {
        cliente: customerId,
        dataAlteracao: Date.now(),
        listaEquipe: [],
        local: place.id as string,
        nome: name,
        unidades: []
      };

      const promise = SetService.newConjunto(token, setObj)
        .then((res) => {
          if (!res) {
            throw new Error('Error creating set');
          }
          return res;
        });

      return toast.promise(
        promise,
        {
          pending: t('places.salvando'),
          success: `${hierarchyLabel.conjunto} ${t('created')}!`,
          error: t('Services.ServiceSets.errorCreatingSet')
        },
        {
          toastId: `create-set-${name}`
        }
      ).then((res) => {
        setModalOpen(false);
        queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
        return res;
      }).catch((err) => {
        throw err;
      });
    },
    onSuccess: () => trackEventMatomo('gestão de locais', 'sucesso', 'criação', 'conjunto criado'),
    onError: () => trackEventMatomo('gestão de locais', 'erro', 'criação', 'falha ao criar conjunto'),
  }
  );

  const { mutate: clone, isPending: isCloning } = useMutation({
    mutationKey: ['CLONE_PLACE'],
    mutationFn: async (placeid: string) => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');

      const promise = PlacesServices.duplicateLocal(token, placeid)
        .then((response) => {
          if (!response) {
            throw new Error('Error duplicating place');
          }
          return response;
        });

      return toast.promise(
        promise,
        {
          pending: t('Services.ServicePlaces.copyingPleaseWait'),
          success: t('Services.ServicePlaces.duplicatedLocation'),
          error: t('Services.ServicePlaces.errorDuplicating')
        },
        {
          toastId: `clone-place-${placeid}`
        }
      );
    },
    onSuccess: (response) => {
      trackEventMatomo('gestão de locais', 'sucesso', 'duplicação', 'local duplicado');
      placesList && setPlacesList([
        ...placesList,
        Object.values(response?.updateLocal)[0]
      ]);
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
    },
    onError: () => trackEventMatomo('gestão de locais', 'erro', 'duplicação', 'falha ao duplicar local'),
  });

  const onLoading = isRemoving || isPendingCreate || isCloning;

  const nameCreate = 'conjunto';
  const nameRemove = 'local';

  const switchSort = (value: string, a: ConjuntoType, b: ConjuntoType) => {
    switch (value) {
      case 'crescent':
        return (a.nome?.toLowerCase() > b.nome?.toLowerCase());
      case 'decrescent':
        return (a.nome?.toLowerCase() < b.nome?.toLowerCase());
      default: return (a.nome?.toLowerCase() > b.nome?.toLowerCase());
    }
  };

  return (
    <>
      {associationModal
        && <PlaceTypeAssoci
          place={place}
          open={associationModal}
          setOpen={setAssociationModal}
        />}
      {openRemove && <RemoveModal
        mutate={remove}
        openRemove={openRemove}
        setOpenRemove={setOpenRemove}
        nameRemove={nameRemove}
      />}
      {modalOpen
        && <NewModal
          isDisabled={onLoading}
          nameCreate={nameCreate}
          create={create}
          setModalOpen={setModalOpen}
          modalOpen={modalOpen}
        />}
      {open &&
        <NewPlaceModal
          local={place}
          open={open}
          setOpen={setOpen}
          customerId={customerId} />}
      <Accordion expanded={expanded === place.id} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon onClick={(isExpanded) => {
            trackEventMatomo('tela de locais', 'click', 'acordeon', 'abre/fecha');
            if (expanded === place.id) {
              handleChange(!isExpanded, '');
            } else handleChange(isExpanded, place.id);
          }} />}
          aria-controls={place.id}
          id={place.id}>
          <Stack direction='row' width='100%' justifyContent='space-between' alignItems='flex-start' padding={1}>
            <Stack
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={0.5}
            >
              <Typography color='success' variant='h6' fontWeight={600}>
                {place.nome}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {`${t('Services.ServicePlaces.createdOn')} 
                ${moment(place.dataCadastro).format('DD/MM/YYYY | HH:mm')} 
                ${t('Services.ServicePlaces.by')} 
                ${place.userOwner ? place.userOwner : '--'}`}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {place.localizacao}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Tooltip title={`${t('teamManage.register.kindOfService')}`}>
                <span>
                  <IconButton
                    disabled={onLoading}
                    onClick={() => {
                      trackEventMatomo('tela de locais', 'click', 'button', 'abre modal');
                      setAssociationModal(true);
                    }}
                    aria-label="services">
                    <FaNetworkWired size={25} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`${t('edit')} ${hierarchyLabel.local ?? 'Local'}`}>
                <span>
                  <IconButton
                    disabled={onLoading}
                    onClick={() => {
                      trackEventMatomo('tela de locais', 'click', 'button', 'abre modal');
                      setOpen(true);
                    }}
                    aria-label="edit" color='info'>
                    <AiFillTool size={25} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`${t('checklists.checklists.clone')} ${hierarchyLabel.local}`}>
                <span>
                  <IconButton
                    disabled={onLoading}
                    onClick={() => clone(place?.id ?? '')}
                    aria-label="edit" color='success'>
                    <AiFillCopy size={25} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`${t('delete')} ${hierarchyLabel.local ?? 'Local'}`}>
                <span>
                  <IconButton
                    disabled={onLoading}
                    onClick={() => {
                      trackEventMatomo('tela de locais', 'click', 'button', 'deleta local');
                      setOpenRemove(true);
                    }}
                    aria-label="delete" color='error'>
                    <AiFillDelete size={25} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </AccordionSummary>
        <Stack direction='column' spacing={2} alignItems='flex-start' justifyContent='flex-start' padding={2}>
          <Button
            disabled={onLoading}
            endIcon={<AddCircleIcon />}
            variant='contained'
            color='success'
            onClick={() => {
              trackEventMatomo('tela de locais', 'click', 'button', 'abre modal');
              setModalOpen(!modalOpen);
            }}
          >
            {`${t('add')} ${hierarchyLabel.conjunto ?? 'Conjunto'}`}
          </Button>
          <Stack
            width='100%'
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={1}
          >
            <FormControl sx={{ width: '300px' }}>
              <InputLabel >{t('Services.servicesManagement.sortBy')}</InputLabel>
              <Select
                disabled={!place.conjuntosList?.length || onLoading}
                value={reorderBy ?? ''}
                label={t('Services.servicesManagement.sortBy')}
                onChange={(event) => {
                  setReorderBy(event.target.value);
                  setReorderBy(event.target.value);
                  local[place?.id ?? ''] = event.target.value;
                  setLocal(local);
                  localStorage.setItem('localOrder', JSON.stringify(local));
                }}
              >
                <MenuItem value={'crescent'}>{t('Services.servicesManagement.alphabeticalAscending')} (A-Z)</MenuItem>
                <MenuItem value={'decrescent'}>{t('Services.servicesManagement.alphabeticalDescending')} (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
        {expanded === place.id
          && place?.conjuntosList?.length
          ? place?.conjuntosList
            ?.sort((conjuntoA, conjuntoB) => { return switchSort(reorderBy, conjuntoA, conjuntoB) ? 1 : - 1; })
            ?.map((e, i) => (
              <SetAcordeon
                key={e.id}
                local={local}
                setLocal={setLocal}
                set={e}
                placeId={place?.id ?? ''}
                selected={selected}
                conjuntosList={place?.conjuntosList ?? []}
                expandedConjs={expandedConjs}
                setExpandedConjs={setExpandedConjs}
                index={i}
              />
            )) : <Typography padding={2} textAlign='center' color='textDisabled' fontWeight={600}>{
              `${t('places.este')} ${hierarchyLabel.local} ${t('places.aindaNaoTem')} ${hierarchyLabel.conjunto} ${t('places.adicionado')}`}
          </Typography>}
      </Accordion>
    </>
  );
}
